import { PropsWithChildren, useEffect } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppInsightsContext, AppInsightsErrorBoundary, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import ApplicationError from '../Components/ErrorHandling/ApplicationError';
import { ApplicationErrorType } from '../Components/ErrorHandling/types';
import useEntraAccount from '../../Hooks/useEntraAccount';

type AppInsightsProps = PropsWithChildren & {
	appInsightsInstance?: ApplicationInsights;
	reactPlugin: ReactPlugin;
};

/**
 * Wrapper component responsible for rendering the App Insights Provider and Error Boundary when an instance of App Insights
 * is present in the application. When there is not, simply continues rendering the component tree.
 */
export default function AppInsights(props: AppInsightsProps) {
	const { appInsightsInstance } = props;
	const { samAccountName } = useEntraAccount();

	useEffect(() => {
		if (appInsightsInstance && samAccountName) {
			appInsightsInstance.context.user.setAuthenticatedUserContext(samAccountName, undefined, true);
		}
	}, [samAccountName, appInsightsInstance]);

	if (appInsightsInstance) {
		return (
			<AppInsightsContext.Provider value={props.reactPlugin}>
				<AppInsightsErrorBoundary
					appInsights={props.reactPlugin}
					onError={() => {
						return <ApplicationError type={ApplicationErrorType.Unexpected} />;
					}}
				>
					<>{props.children}</>
				</AppInsightsErrorBoundary>
			</AppInsightsContext.Provider>
		);
	}

	return <>{props.children}</>;
}
